
import React, { useState, useMemo, useEffect } from 'react';
import { useInventoryStats } from '../../../hooks/useInventoryStats';
import { AppUser, UserRole, Property } from '../../../types';
import { 
    ChartBarIcon, 
    ArrowDownTrayIcon, 
    FunnelIcon, 
    HomeModernIcon, 
    UsersIcon, 
    CurrencyDollarIcon,
    ExclamationCircleIcon,
    ArrowPathIcon
} from '../../../components/Icons';
import { downloadInventoryCSV } from '../../../utils/csvUtils';

interface AdminInventoryStatsSectionProps {
    appUser: AppUser | null;
}

export const AdminInventoryStatsSection: React.FC<AdminInventoryStatsSectionProps> = ({ appUser }) => {
    const { allProperties, isLoading, error, fetchAllActiveProperties } = useInventoryStats(appUser);
    
    // Filters state
    const [cityFilter, setCityFilter] = useState<string>('');
    const [typeFilter, setTypeFilter] = useState<string>('');
    const [ownerFilter, setOwnerFilter] = useState<string>('');
    const [priceRange, setPriceRange] = useState<{ min: string, max: string }>({ min: '', max: '' });

    useEffect(() => {
        fetchAllActiveProperties();
    }, [fetchAllActiveProperties]);

    // Options for filters
    const cities = useMemo(() => {
        const vals = Array.from(new Set(allProperties.map(p => p.ciudad).filter(Boolean)));
        return vals.sort((a, b) => String(a).localeCompare(String(b)));
    }, [allProperties]);

    const types = useMemo(() => {
        const vals = Array.from(new Set(allProperties.map(p => p.tipoinmueble).filter(Boolean)));
        return vals.sort((a, b) => String(a).localeCompare(String(b)));
    }, [allProperties]);

    const owners = useMemo(() => {
        const vals = Array.from(new Set(allProperties.map(p => p.owner).filter(Boolean)));
        return vals.sort((a, b) => String(a).localeCompare(String(b)));
    }, [allProperties]);

    // Filtered properties
    const filteredProperties = useMemo(() => {
        return allProperties.filter(p => {
            if (cityFilter && p.ciudad !== cityFilter) return false;
            if (typeFilter && p.tipoinmueble !== typeFilter) return false;
            if (ownerFilter && p.owner !== ownerFilter) return false;
            if (priceRange.min && p.precio_venta < Number(priceRange.min)) return false;
            if (priceRange.max && p.precio_venta > Number(priceRange.max)) return false;
            return true;
        });
    }, [allProperties, cityFilter, typeFilter, ownerFilter, priceRange]);

    // Metrics calculation
    const metrics = useMemo(() => {
        const total = filteredProperties.length;
        const visibleBroker = filteredProperties.filter(p => p.es_visible_broker).length;
        const premiumOnly = filteredProperties.filter(p => p.visible_roles?.includes(UserRole.BROKER_PREMIUM) && !p.es_visible_broker).length;
        const zeroCommission = filteredProperties.filter(p => !p.commissionRate || p.commissionRate === 0).length;
        const manualCreated = filteredProperties.filter(p => p.creado_manualmente).length;

        return { total, visibleBroker, premiumOnly, zeroCommission, manualCreated };
    }, [filteredProperties]);

    const handleExportCsv = () => {
        const prefix = cityFilter || typeFilter ? "Inventario_Filtrado" : "Inventario_Total";
        downloadInventoryCSV(filteredProperties, prefix);
    };

    const StatCard = ({ title, value, icon, colorClass, subtitle }: { title: string, value: number, icon: React.ReactNode, colorClass: string, subtitle?: string }) => (
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-all">
            <div className="flex items-start justify-between mb-4">
                <div className={`p-3 rounded-2xl ${colorClass}`}>
                    {icon}
                </div>
                <span className="text-2xl font-black text-gray-900">{value.toLocaleString()}</span>
            </div>
            <h4 className="text-sm font-bold text-gray-500 uppercase tracking-wider">{title}</h4>
            {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
    );

    if (error) {
        return (
            <div className="p-8 text-center bg-red-50 rounded-3xl border border-red-100">
                <ExclamationCircleIcon className="w-12 h-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-bold text-red-900">Error al cargar datos</h3>
                <p className="text-red-600 mb-6">{error}</p>
                <button 
                    onClick={fetchAllActiveProperties}
                    className="px-6 py-2 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-all flex items-center gap-2 mx-auto"
                >
                    <ArrowPathIcon className="w-4 h-4" /> Reintentar
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-8 animate-fade-in">
            {/* Header & Actions */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-black text-gray-900 uppercase tracking-tight flex items-center gap-3">
                        <ChartBarIcon className="w-8 h-8 text-[#0073b7]" />
                        Salud del Inventario
                    </h2>
                    <p className="text-gray-500 text-sm font-medium">Análisis de visibilidad y calidad del catálogo activo.</p>
                </div>
                <div className="flex items-center gap-3">
                    <button 
                        onClick={fetchAllActiveProperties}
                        disabled={isLoading}
                        className="p-3 bg-gray-100 text-gray-600 rounded-2xl hover:bg-gray-200 transition-all disabled:opacity-50"
                        title="Actualizar datos"
                    >
                        <ArrowPathIcon className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
                    </button>
                    <button 
                        onClick={handleExportCsv}
                        disabled={isLoading || filteredProperties.length === 0}
                        className="flex items-center gap-2 px-6 py-3 bg-[#0073b7] text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-sky-700 transition-all shadow-lg shadow-sky-100 disabled:opacity-50"
                    >
                        <ArrowDownTrayIcon className="w-4 h-4" />
                        Exportar CSV ({filteredProperties.length})
                    </button>
                </div>
            </div>

            {/* Filters Section */}
            <div className="bg-gray-50 p-6 rounded-3xl border border-gray-100">
                <div className="flex items-center gap-2 mb-6 text-gray-900 font-bold uppercase text-xs tracking-widest">
                    <FunnelIcon className="w-4 h-4" />
                    Filtros de Análisis
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="space-y-1">
                        <label className="text-[10px] font-black text-gray-400 uppercase ml-2">Ciudad</label>
                        <select 
                            value={cityFilter}
                            onChange={(e) => setCityFilter(e.target.value)}
                            className="w-full bg-white border-none rounded-2xl px-4 py-3 text-sm font-bold text-gray-700 shadow-sm focus:ring-2 focus:ring-sky-500"
                        >
                            <option value="">Todas las ciudades</option>
                            {cities.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-black text-gray-400 uppercase ml-2">Tipo de Inmueble</label>
                        <select 
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                            className="w-full bg-white border-none rounded-2xl px-4 py-3 text-sm font-bold text-gray-700 shadow-sm focus:ring-2 focus:ring-sky-500"
                        >
                            <option value="">Todos los tipos</option>
                            {types.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-black text-gray-400 uppercase ml-2">Fuente / Propietario</label>
                        <select 
                            value={ownerFilter}
                            onChange={(e) => setOwnerFilter(e.target.value)}
                            className="w-full bg-white border-none rounded-2xl px-4 py-3 text-sm font-bold text-gray-700 shadow-sm focus:ring-2 focus:ring-sky-500"
                        >
                            <option value="">Todas las fuentes</option>
                            {owners.map(o => <option key={o} value={o}>{o.replace(/-/g, ' ')}</option>)}
                        </select>
                    </div>
                    <div className="space-y-1">
                        <label className="text-[10px] font-black text-gray-400 uppercase ml-2">Rango de Precio (Min)</label>
                        <input 
                            type="number"
                            placeholder="Mínimo"
                            value={priceRange.min}
                            onChange={(e) => setPriceRange(prev => ({ ...prev, min: e.target.value }))}
                            className="w-full bg-white border-none rounded-2xl px-4 py-3 text-sm font-bold text-gray-700 shadow-sm focus:ring-2 focus:ring-sky-500"
                        />
                    </div>
                </div>
                {(cityFilter || typeFilter || ownerFilter || priceRange.min || priceRange.max) && (
                    <button 
                        onClick={() => {
                            setCityFilter('');
                            setTypeFilter('');
                            setOwnerFilter('');
                            setPriceRange({ min: '', max: '' });
                        }}
                        className="mt-4 text-xs font-bold text-sky-600 hover:text-sky-700 flex items-center gap-1 ml-2"
                    >
                        Limpiar filtros
                    </button>
                )}
            </div>

            {/* Stats Grid */}
            {isLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    {[1, 2, 3, 4].map(i => (
                        <div key={i} className="h-32 bg-gray-100 rounded-3xl animate-pulse" />
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <StatCard 
                        title="Inventario Total" 
                        value={metrics.total} 
                        icon={<HomeModernIcon className="w-6 h-6 text-sky-600" />} 
                        colorClass="bg-sky-50"
                        subtitle="Inmuebles activos en sistema"
                    />
                    <StatCard 
                        title="Alcance Broker" 
                        value={metrics.visibleBroker} 
                        icon={<UsersIcon className="w-6 h-6 text-emerald-600" />} 
                        colorClass="bg-emerald-50"
                        subtitle="Visibles para Broker estándar"
                    />
                    <StatCard 
                        title="Exclusividad Premium" 
                        value={metrics.premiumOnly} 
                        icon={<CurrencyDollarIcon className="w-6 h-6 text-amber-600" />} 
                        colorClass="bg-amber-50"
                        subtitle="Solo para Premium / Internos"
                    />
                    <StatCard 
                        title="Puntos Ciegos" 
                        value={metrics.zeroCommission} 
                        icon={<ExclamationCircleIcon className="w-6 h-6 text-rose-600" />} 
                        colorClass="bg-rose-50"
                        subtitle="Comisión 0% o sin visibilidad"
                    />
                </div>
            )}

            {/* Detailed Table Preview (Optional but good for context) */}
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-gray-50 flex items-center justify-between">
                    <h3 className="text-sm font-black text-gray-900 uppercase tracking-widest">Vista Previa del Inventario</h3>
                    <span className="text-[10px] font-bold bg-gray-100 text-gray-500 px-3 py-1 rounded-full uppercase">
                        Mostrando {Math.min(filteredProperties.length, 50)} de {filteredProperties.length}
                    </span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-50/50">
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-wider">Código</th>
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-wider">Ciudad</th>
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-wider">Precio</th>
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-wider">Comisión</th>
                                <th className="px-6 py-4 text-[10px] font-black text-gray-400 uppercase tracking-wider">Visibilidad</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                            {filteredProperties.slice(0, 50).map(p => (
                                <tr key={p.id} className="hover:bg-gray-50/50 transition-colors">
                                    <td className="px-6 py-4 text-xs font-bold text-gray-900">{p.codigo}</td>
                                    <td className="px-6 py-4 text-xs text-gray-500">{p.ciudad}</td>
                                    <td className="px-6 py-4 text-xs font-bold text-gray-900">${p.precio_venta?.toLocaleString()}</td>
                                    <td className="px-6 py-4 text-xs text-gray-500">{(p.commissionRate * 100).toFixed(1)}%</td>
                                    <td className="px-6 py-4">
                                        <div className="flex gap-1">
                                            {p.es_visible_broker && <span className="text-[8px] font-black bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full uppercase">Broker</span>}
                                            {p.visible_roles?.includes(UserRole.BROKER_PREMIUM) && <span className="text-[8px] font-black bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full uppercase">Premium</span>}
                                            {p.creado_manualmente && <span className="text-[8px] font-black bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full uppercase">Manual</span>}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
